import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getDocumentTree,
  relateDocument,
  unrelateDocument,
  renewDocument,
} from '../services/documentsApi'
import type { DocumentTreeNode } from '../services/documentsApi'

/** Fetch document tree (parent + children hierarchy) */
export function useDocumentTree(koblinkId: string | undefined, actorId?: string) {
  return useQuery<DocumentTreeNode>({
    queryKey: ['documentTree', koblinkId, actorId],
    queryFn: () => getDocumentTree(koblinkId!, actorId),
    enabled: !!koblinkId,
    staleTime: 60_000,
  })
}

/** Link a document as child of another */
export function useRelateDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      koblinkId,
      parentKoblinkId,
      relationType,
      actorId,
    }: {
      koblinkId: string
      parentKoblinkId: string
      relationType: 'amendment' | 'renewal' | 'related'
      actorId?: string
    }) => relateDocument(koblinkId, parentKoblinkId, relationType, actorId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['documentTree', variables.koblinkId] })
      queryClient.invalidateQueries({ queryKey: ['documentTree', variables.parentKoblinkId] })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
  })
}

/** Remove parent relationship from a document */
export function useUnrelateDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      koblinkId,
      actorId,
    }: {
      koblinkId: string
      actorId?: string
    }) => unrelateDocument(koblinkId, actorId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['documentTree', variables.koblinkId] })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
  })
}

/** Create a renewal of an existing document */
export function useRenewDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      koblinkId,
      title,
      actorId,
    }: {
      koblinkId: string
      title?: string
      actorId?: string
    }) => renewDocument(koblinkId, title ? { title } : undefined, actorId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['documentTree', variables.koblinkId] })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
  })
}
